import type { BaseResponse, Event, KnownPackageName, Package } from "@anggunpraya-templates/types";

interface PreviewPackageMeta {
  isRecommended: boolean;
  description: string;
  badges: string[];
}

export const PREVIEW_PACKAGE_QUERY_KEY = "preview-package";
export const PREVIEW_PACKAGE_MODES: KnownPackageName[] = ["Intimate", "Celebration"];
export const DEFAULT_PREVIEW_PACKAGE_MODE: KnownPackageName = "Celebration";

export const PREVIEW_PACKAGE_MODE_META: Record<KnownPackageName, PreviewPackageMeta> = {
  Intimate: {
    isRecommended: false,
    description: "Mode publik yang lebih ringkas tanpa dashboard tamu dan RSVP.",
    badges: ["Publik", "Tanpa RSVP", "Tanpa dashboard tamu"],
  },
  Celebration: {
    isRecommended: true,
    description: "Mode tamu pribadi dengan dashboard, RSVP, dan tracking buka undangan.",
    badges: ["Dashboard tamu", "RSVP aktif", "Guest-based flow"],
  },
};

export function resolvePreviewPackageMode(value: unknown, fallback?: unknown): KnownPackageName {
  return normalizePreviewPackageMode(value)
    ?? normalizePreviewPackageMode(fallback)
    ?? DEFAULT_PREVIEW_PACKAGE_MODE;
}

export function createPreviewPackageDetail(mode: KnownPackageName, basePackage?: Partial<Package> | null): Partial<Package> {
  const isIntimate = mode === "Intimate";

  return {
    ...basePackage,
    name: mode,
    include_user_dashboard: !isIntimate,
    include_rsvp: !isIntimate,
  };
}

export function applyPreviewPackageModeToEventDetail(
  eventDetail: BaseResponse<Event>,
  mode: unknown,
) {
  const resolvedMode = resolvePreviewPackageMode(mode, eventDetail.data?.package?.name);

  return {
    ...eventDetail,
    data: {
      ...eventDetail.data,
      package: {
        ...eventDetail.data?.package,
        ...createPreviewPackageDetail(resolvedMode, eventDetail.data?.package),
      },
    },
  } as BaseResponse<Event>;
}

export function buildPreviewPackageUrl(href: string, mode: KnownPackageName) {
  const url = new URL(href);
  const currentMode = resolvePreviewPackageMode(url.searchParams.get(PREVIEW_PACKAGE_QUERY_KEY));

  if (mode === currentMode) {
    url.searchParams.delete(PREVIEW_PACKAGE_QUERY_KEY);
  }
  else {
    url.searchParams.set(PREVIEW_PACKAGE_QUERY_KEY, mode.toLowerCase());
  }

  return url.toString();
}

function normalizePreviewPackageMode(value: unknown): KnownPackageName | null {
  const rawValue = Array.isArray(value) ? value[0] : value;

  if (typeof rawValue !== "string") {
    return null;
  }

  const normalizedValue = rawValue.trim().toLowerCase();
  return PREVIEW_PACKAGE_MODES.find(mode => mode.toLowerCase() === normalizedValue) ?? null;
}

export function usePreviewPackageMode() {
  const { flavor } = useRuntimeConfig().public;
  const route = useRoute();

  const isPreviewFlavor = computed(() => flavor === "preview");
  const previewPackageName = computed<KnownPackageName>(() => resolvePreviewPackageMode(route.query[PREVIEW_PACKAGE_QUERY_KEY]));
  const previewPackageMeta = computed(() => PREVIEW_PACKAGE_MODE_META[previewPackageName.value]);

  async function reloadWithPreviewPackage(mode: KnownPackageName) {
    if (!import.meta.client || !isPreviewFlavor.value || mode === previewPackageName.value) {
      return;
    }

    const { pathname, search, hash } = new URL(buildPreviewPackageUrl(window.location.href, mode));

    reloadNuxtApp({
      path: `${pathname}${search}${hash}`,
      force: true,
      ttl: 0,
    });
  }

  return {
    isPreviewFlavor,
    previewPackageName,
    previewPackageModes: PREVIEW_PACKAGE_MODES,
    previewPackageMeta,
    reloadWithPreviewPackage,
  };
}
